import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeContext } from './context/ThemeContext';

// Import components
import Header from './components/Header';
import Footer from './components/Footer';

// Import pages
import MainPage from './pages/MainPage';
import Destinations from './pages/Destinations';
import DestinationDetail from './pages/DestinationDetail';
import Stories from './pages/Stories';
import About from './pages/About';
import Team from './pages/Team';

function App() {
  const [theme, setTheme] = useState('light');
  const [favorites, setFavorites] = useState([]);

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  }, []);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const addToFavorites = (destination) => {
    setFavorites(prev => {
      const exists = prev.find(fav => fav.slug === destination.slug);
      if (!exists) {
        return [...prev, destination];
      }
      return prev;
    });
  };

  const removeFromFavorites = (slug) => {
    setFavorites(prev => prev.filter(fav => fav.slug !== slug));
  };

  const isFavorite = (slug) => {
    return favorites.some(fav => fav.slug === slug);
  };

  const themeContextValue = {
    theme,
    toggleTheme,
    favorites,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
  };

  return (
    <HelmetProvider>
      <ThemeContext.Provider value={themeContextValue}>
        <Router>
          <div className={`min-h-screen bg-secondary-50 dark:bg-secondary-900 transition-colors duration-200 ${theme}`}>
            <Header />
            <main className="flex-1">
              <AnimatePresence mode="wait">
                <Routes>
                  <Route 
                    path="/" 
                    element={
                      <motion.div 
                        key="home" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <MainPage />
                      </motion.div>
                    } 
                  />
                  <Route 
                    path="/destinations" 
                    element={
                      <motion.div 
                        key="destinations" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <Destinations />
                      </motion.div>
                    } 
                  />
                  <Route 
                    path="/destinations/:slug" 
                    element={
                      <motion.div 
                        key="destination-detail" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <DestinationDetail />
                      </motion.div>
                    } 
                  />
                  <Route 
                    path="/stories" 
                    element={
                      <motion.div 
                        key="stories" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <Stories />
                      </motion.div>
                    } 
                  />
                  <Route 
                    path="/about" 
                    element={
                      <motion.div 
                        key="about" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <About />
                      </motion.div>
                    } 
                  />
                  <Route 
                    path="/team" 
                    element={
                      <motion.div 
                        key="team" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        exit={{ opacity: 0 }} 
                        transition={{ duration: 0.3 }}
                      >
                        <Team />
                      </motion.div>
                    } 
                  />
                </Routes>
              </AnimatePresence>
            </main>
            <Footer />
          </div>
        </Router>
      </ThemeContext.Provider>
    </HelmetProvider>
  );
}

export default App;
